/**
 * @fileoverview UploadStep Component - File Selection and Validation
 *
 * This component handles the upload step (Step 0) of the BlurMe workflow.
 * It provides drag-and-drop and click-to-browse file upload functionality
 * with validation for file types, sizes, and upload limits.
 *
 * @description
 * Key features:
 * - Drag-and-drop file upload
 * - Click-to-browse file selection
 * - File type validation (images only, no GIFs)
 * - File size validation
 * - Upload limit enforcement (per-upload and daily limits)
 * - Preview thumbnails with remove functionality
 *
 * @requires React
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';

/**
 * UploadStep Component
 *
 * @param {Object} props
 * @param {Array} props.files - Array of currently uploaded file objects
 * @param {Function} props.onFilesChange - Callback to update files state
 * @param {number} props.maxSizeMB - Maximum file size in megabytes
 * @param {number} props.maxFilesPerUpload - Maximum files allowed per upload session
 * @param {number} props.maxFilesPerDay - Maximum files allowed per day
 * @param {number|null} props.remainingUploads - Number of remaining uploads for the day
 * @param {Function} props.onShowToast - Callback to display toast notifications
 * @param {string} props.uploadMode - Current upload mode ('images' or 'videos')
 * @param {Function} props.onUploadModeChange - Callback to change upload mode
 * @returns {JSX.Element}
 */
function UploadStep({
  files,
  onFilesChange,
  maxSizeMB = 15,
  maxFilesPerUpload = 2,
  maxFilesPerDay = 6,
  remainingUploads = null,
  onShowToast,
  uploadMode = 'images',
  onUploadModeChange
}) {
  /* ========================================================================
     Refs and State
     ======================================================================== */

  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // Use internal state if no external control is provided
  const [internalMode, setInternalMode] = useState('images');
  const currentMode = onUploadModeChange ? uploadMode : internalMode;
  const setCurrentMode = onUploadModeChange || setInternalMode;

  // Video coming soon popup state
  const [showVideoComingSoon, setShowVideoComingSoon] = useState(false);

  // File type configurations based on mode
  const modeConfig = {
    images: {
      title: 'Image(s)',
      accept: 'image/*',
      formats: 'JPG, PNG, WEBP',
      checkType: (f) => f.type.startsWith('image/'),
      rejectGif: true,
      isVideo: false,
      maxPerUpload: maxFilesPerUpload
    },
    videos: {
      title: 'Video',  // Singular since only 1 allowed
      accept: 'video/*',
      formats: 'MP4, MOV, AVI',
      checkType: (f) => f.type.startsWith('video/'),
      rejectGif: false,
      isVideo: true,
      maxPerUpload: 1  // Always 1 video allowed per upload
    }
  };

  const currentConfig = modeConfig[currentMode];

  /**
   * Generate thumbnail from video file
   */
  const generateVideoThumbnail = async (videoFile) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;

      const objectUrl = URL.createObjectURL(videoFile);
      video.src = objectUrl;

      video.onloadedmetadata = () => {
        video.currentTime = Math.min(1, video.duration * 0.1);
      };

      video.onseeked = () => {
        const maxSize = 200;
        let width = video.videoWidth;
        let height = video.videoHeight;

        if (width > height) {
          if (width > maxSize) {
            height = (height / width) * maxSize;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width / height) * maxSize;
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(video, 0, 0, width, height);

        const thumbnail = canvas.toDataURL('image/jpeg', 0.8);
        URL.revokeObjectURL(objectUrl);
        resolve({ thumbnail, duration: video.duration, width: video.videoWidth, height: video.videoHeight });
      };

      video.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve({ thumbnail: null, duration: 0, width: 0, height: 0 });
      };

      setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
        resolve({ thumbnail: null, duration: 0, width: 0, height: 0 });
      }, 10000);
    });
  };

  /* ========================================================================
     File Handling Functions
     ======================================================================== */

  /**
   * Process and validate uploaded files
   * Checks for valid file types, sizes, and upload limits
   *
   * @param {FileList} fileList - The files selected by the user
   */
  const handleFiles = useCallback((fileList) => {
    const maxAllowed = currentConfig.maxPerUpload;
    const remainingSlots = maxAllowed - files.length;

    if (remainingSlots <= 0) {
      const typeLabel = currentConfig.isVideo ? 'video' : 'files';
      onShowToast?.(`Maximum ${maxAllowed} ${typeLabel} allowed per upload`, 'error');
      return;
    }

    const validFiles = [];
    let rejectedGifs = 0;
    let rejectedTooLarge = 0;
    let rejectedInvalidType = 0;

    Array.from(fileList).forEach(f => {
      // Reject GIF files (including animated GIFs) - only for images mode
      if (currentConfig.rejectGif && (f.type === 'image/gif' || f.name.toLowerCase().endsWith('.gif'))) {
        rejectedGifs++;
        return;
      }

      // Check file type based on current mode
      if (!currentConfig.checkType(f)) {
        rejectedInvalidType++;
        return;
      }

      // Check file size
      if (f.size > maxSizeMB * 1024 * 1024) {
        rejectedTooLarge++;
        return;
      }

      validFiles.push(f);
    });

    // Show error messages for rejected files
    if (rejectedGifs > 0) {
      onShowToast?.(`Animated GIFs are not supported. ${rejectedGifs} file(s) rejected.`, 'error');
    }
    if (rejectedTooLarge > 0) {
      onShowToast?.(`${rejectedTooLarge} file(s) too large (max ${maxSizeMB}MB each).`, 'error');
    }
    if (rejectedInvalidType > 0) {
      onShowToast?.(`${rejectedInvalidType} file(s) rejected (only ${currentConfig.title.toLowerCase()} allowed).`, 'error');
    }

    // Process files differently for videos vs images
    const processFiles = async () => {
      const newFiles = [];
      const maxToProcess = Math.min(remainingSlots, currentConfig.maxPerUpload);

      for (const file of validFiles.slice(0, maxToProcess)) {
        if (currentConfig.isVideo) {
          // Generate video thumbnail and get metadata
          const { thumbnail, duration, width, height } = await generateVideoThumbnail(file);
          newFiles.push({
            file,
            name: file.name,
            size: file.size,
            preview: URL.createObjectURL(file),
            thumbnail: thumbnail,
            isVideo: true,
            metadata: {
              duration_seconds: duration,
              width,
              height
            },
            detections: [],
            blurSettings: {}
          });
        } else {
          newFiles.push({
            file,
            name: file.name,
            size: file.size,
            preview: URL.createObjectURL(file),
            isVideo: false,
            detections: [],
            blurSettings: {}
          });
        }
      }

      if (validFiles.length > maxToProcess) {
        const typeLabel = currentConfig.isVideo ? 'video' : 'file(s)';
        onShowToast?.(`Only ${maxToProcess} ${typeLabel} added. Max ${currentConfig.maxPerUpload} per upload`, 'info');
      }

      if (newFiles.length > 0) {
        onShowToast?.(`Uploaded - Please proceed...`, 'success');
      }

      onFilesChange(prev => [...prev, ...newFiles]);
    };

    processFiles();
  }, [onFilesChange, maxSizeMB, files.length, onShowToast, currentConfig]);

  /**
   * Handle file drop event from drag-and-drop
   * @param {DragEvent} e - The drop event
   */
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  /**
   * Remove a file from the upload queue
   * Revokes the object URL to prevent memory leaks
   *
   * @param {number} index - Index of the file to remove
   */
  const removeFile = (index) => {
    onFilesChange(prev => {
      const file = prev[index];
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  /* ========================================================================
     Render
     ======================================================================== */

  return (
    <div className="max-w-3xl mx-auto">
      {/* ================================================================
          Header Section
          - Title with Image/Video toggle
          - Description and privacy notice
          ================================================================ */}
      {/* Video Coming Soon Popup */}
      {showVideoComingSoon && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShowVideoComingSoon(false)}
        >
          <div
            className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 max-w-sm mx-4 border border-white/10 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <span className="text-3xl">🎬</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Video Blur Coming Soon!</h3>
              <p className="text-gray-400 mb-4">We appreciate your anticipation! AI Video blur feature with person and gender detection is currently in development.</p>
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 mb-4">
                <p className="text-emerald-300 text-sm">🚀 Stay tuned for updates</p>
                <p className="text-gray-400 text-xs mt-2 italic">(Will Toggle Back to Image Blur for now)</p>
              </div>
              <button
                onClick={() => setShowVideoComingSoon(false)}
                className="w-full py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-medium transition-colors text-white"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="text-center mb-4">
        <h2 className="text-3xl font-bold mb-2 flex items-center justify-center gap-2">
          <span>Blur Your</span>
          <button
            onClick={() => {
              if (files.length === 0) {
                // Show popup when trying to switch to videos
                if (currentMode === 'images') {
                  setShowVideoComingSoon(true);
                } else {
                  setCurrentMode('images');
                }
              } else {
                onShowToast?.('Clear files before switching mode', 'error');
              }
            }}
            className="relative inline-flex items-center text-emerald-400 hover:text-emerald-300 transition-colors cursor-pointer group"
            title="Click to toggle between Images and Videos"
          >
            <span className="border-b-2 border-dashed border-emerald-400 group-hover:border-emerald-300">
              {currentConfig.title}
            </span>
            <span className="ml-2 flex flex-col items-center justify-center bg-emerald-500/20 rounded-md px-1.5 py-0.5 group-hover:bg-emerald-500/30 transition-colors">
              <svg
                className="w-3 h-3 text-emerald-400 -mb-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={3}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
              </svg>
              <svg
                className="w-3 h-3 text-emerald-400 -mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={3}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </button>
        </h2>
        <p className="text-gray-400">LetsBlur protects your privacy by automatically detecting and blurring people in images and videos using AI.</p>
        <p className="text-emerald-400/80 text-sm mt-2 font-medium">
          🔒 Uploaded content is processed in-memory and never stored on our servers. 🔒
        </p>
      </div>

      {/* ================================================================
          Drop Zone
          - Drag-and-drop area
          - Click to browse trigger
          - Visual feedback for drag state
          ================================================================ */}
      <div
        className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all min-h-[280px] flex flex-col justify-center ${
          isDragging
            ? 'border-emerald-500 bg-emerald-500/10'
            : 'border-emerald-500/40 hover:border-emerald-500/80 hover:bg-white/5'
        }`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
          <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold mb-2">Drop {currentConfig.title.toLowerCase()} here or click to browse</h3>
        <p className="text-gray-400">{currentConfig.formats} (max {maxSizeMB}MB each)</p>
        <p className="text-gray-400 text-xs mt-3">
          Max {currentConfig.maxPerUpload} {currentConfig.isVideo ? 'video' : 'uploads'} allowed ({maxFilesPerDay} per day) - Contact Us for Custom Solutions
        </p>
        {remainingUploads !== null && (
          <p className={`font-bold text-sm mt-2 ${remainingUploads === 0 ? 'text-red-500' : 'text-indigo-400'}`}>
            ({remainingUploads} remaining today)
          </p>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple={!currentConfig.isVideo}
          accept={currentConfig.accept}
          className="hidden"
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {/* ================================================================
          File Preview Grid (Videos only - Images use OneClickBlur component)
          - Thumbnail previews of selected files
          - Remove buttons
          - Clear all option
          ================================================================ */}
      {files.length > 0 && currentConfig.isVideo && (
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <span className="text-sm text-gray-400">Selected ({files.length})</span>
            <button
              onClick={() => {
                files.forEach(f => f.preview && URL.revokeObjectURL(f.preview));
                onFilesChange([]);
              }}
              className="text-sm text-red-400 hover:text-red-300"
            >
              Clear All
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {files.map((file, i) => (
              <div key={i} className="bg-white/5 backdrop-blur rounded-xl p-3 relative group">
                {file.isVideo ? (
                  <div className="relative">
                    <img
                      src={file.thumbnail || file.preview}
                      alt=""
                      className="w-full h-24 object-cover rounded-lg mb-2"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 bg-black/50 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                    {file.metadata?.duration_seconds && (
                      <span className="absolute bottom-3 right-1 bg-black/70 text-white text-xs px-1 rounded">
                        {Math.floor(file.metadata.duration_seconds / 60)}:{String(Math.floor(file.metadata.duration_seconds % 60)).padStart(2, '0')}
                      </span>
                    )}
                  </div>
                ) : (
                  <img src={file.preview} alt="" className="w-full h-24 object-cover rounded-lg mb-2" />
                )}
                <p className="text-sm truncate">{file.name}</p>
                <button
                  onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                  className="absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors text-white text-sm shadow-lg"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default UploadStep;
